import { useEffect, useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { tenantApi } from "@/lib/apiClient";

const PROVIDERS = [
  { id: "openai", label: "OpenAI", placeholder: "sk-…" },
  { id: "anthropic", label: "Anthropic", placeholder: "sk-ant-…" },
];

export const LlmKeySettings = ({ disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [keys, setKeys] = useState({ openai: "", anthropic: "" });
  const [saving, setSaving] = useState("");

  const load = async () => {
    try {
      const r = await tenantApi.get("/tenant/llm-credentials");
      setStatuses(r.data.providers || []);
    } catch (e) {
      toast.error("Could not load provider key status.");
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const save = async (provider) => {
    const apiKey = keys[provider].trim();
    if (!apiKey) return;
    setSaving(provider);
    try {
      await tenantApi.put(`/tenant/llm-credentials/${provider}`, { api_key: apiKey });
      setKeys((prev) => ({ ...prev, [provider]: "" }));
      await load();
      toast.success(`${provider === "openai" ? "OpenAI" : "Anthropic"} key saved securely.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save provider key.");
    } finally {
      setSaving("");
    }
  };

  const remove = async (provider) => {
    setSaving(provider);
    try {
      await tenantApi.delete(`/tenant/llm-credentials/${provider}`);
      await load();
      toast.success("Provider key removed; platform access will be used.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove provider key.");
    } finally {
      setSaving("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" disabled={disabled} data-testid="llm-key-settings-button">
          <KeyRound className="h-4 w-4" />
          <span className="hidden sm:inline">Provider keys</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="llm-key-settings-dialog">
        <DialogHeader>
          <DialogTitle>Provider keys</DialogTitle>
          <DialogDescription>
            Add tenant-owned OpenAI or Anthropic credentials, or keep platform access.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          {PROVIDERS.map((provider) => {
            const status = statuses.find((item) => item.provider === provider.id);
            return (
              <section key={provider.id} className="space-y-2" data-testid={`llm-key-${provider.id}-section`}>
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor={`key-${provider.id}`} className="text-sm font-semibold">{provider.label}</label>
                  <span className="text-xs text-muted-foreground" data-testid={`llm-key-${provider.id}-status`}>
                    {status?.configured ? status.masked_key : "Platform key"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Input
                    id={`key-${provider.id}`}
                    type="password"
                    autoComplete="new-password"
                    value={keys[provider.id]}
                    onChange={(e) => setKeys((prev) => ({ ...prev, [provider.id]: e.target.value }))}
                    placeholder={provider.placeholder}
                    data-testid={`llm-key-${provider.id}-input`}
                  />
                  <Button
                    onClick={() => save(provider.id)}
                    disabled={saving === provider.id || keys[provider.id].trim().length < 20}
                    data-testid={`llm-key-${provider.id}-save-button`}
                  >
                    Save
                  </Button>
                  {status?.configured && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => remove(provider.id)}
                      disabled={saving === provider.id}
                      aria-label={`Remove ${provider.label} key`}
                      data-testid={`llm-key-${provider.id}-remove-button`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </section>
            );
          })}
          <p className="text-xs text-muted-foreground" data-testid="llm-key-security-note">
            Keys are encrypted at rest and never shown again. Remove a key to return to platform access.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};